/**
 * Normalize hex color (expand shorthand to full format)
 * @param {string} hex - Hex color (e.g., "#39f" or "#3399ff")
 * @returns {string} - Full hex color (e.g., "#3399ff")
 */
export const normalizeHex = (hex) => {
	if (!hex) return null;
	const cleanHex = hex.replace("#", "");
	if (cleanHex.length === 3) {
		// Expand shorthand: #39f -> #3399ff
		return `#${cleanHex
			.split("")
			.map((char) => char + char)
			.join("")}`;
	}
	return hex.startsWith("#") ? hex : `#${cleanHex}`;
};

/**
 * Convert hex color to RGB values
 * @param {string} hex - Hex color (e.g., "#FF5733", "#39f", or "FF5733")
 * @returns {string} - RGB values (e.g., "255, 87, 51")
 */
export const hexToRgb = (hex) => {
	if (!hex) return null;
	const normalizedHex = normalizeHex(hex);
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalizedHex);
	return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
};

/**
 * Lighten a hex color by a percentage
 * @param {string} hex - Hex color (supports shorthand like #39f)
 * @param {number} percent - Amount to lighten (0-1)
 * @returns {string} - Lightened hex color
 */
export const lightenColor = (hex, percent) => {
	if (!hex) return null;
	const normalizedHex = normalizeHex(hex);
	const num = parseInt(normalizedHex.replace("#", ""), 16);
	const amt = Math.round(2.55 * percent * 100);
	const R = (num >> 16) + amt;
	const G = ((num >> 8) & 0x00ff) + amt;
	const B = (num & 0x0000ff) + amt;
	return `#${(
		0x1000000 +
		(R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
		(G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
		(B < 255 ? (B < 1 ? 0 : B) : 255)
	)
		.toString(16)
		.slice(1)
		.toUpperCase()}`;
};

/**
 * Darken a hex color by a percentage
 * @param {string} hex - Hex color (supports shorthand like #39f)
 * @param {number} percent - Amount to darken (0-1)
 * @returns {string} - Darkened hex color
 */
export const darkenColor = (hex, percent) => {
	if (!hex) return null;
	const normalizedHex = normalizeHex(hex);
	const num = parseInt(normalizedHex.replace("#", ""), 16);
	const amt = Math.round(2.55 * percent * 100);
	const R = (num >> 16) - amt;
	const G = ((num >> 8) & 0x00ff) - amt;
	const B = (num & 0x0000ff) - amt;
	return `#${(0x1000000 + (R > 0 ? R : 0) * 0x10000 + (G > 0 ? G : 0) * 0x100 + (B > 0 ? B : 0))
		.toString(16)
		.slice(1)
		.toUpperCase()}`;
};

/**
 * Load custom font from Google Fonts
 * @param {string} fontName - Font name (e.g., "Roboto", "Open Sans")
 */
export const loadWhiteLabelFont = (fontName) => {
	if (!fontName) return;

	const fontLinkId = "white-label-font";

	// Remove existing font link if present
	const existingLink = document.getElementById(fontLinkId);
	if (existingLink) {
		existingLink.remove();
	}

	// Create Google Fonts URL
	const fontNameEncoded = fontName.replace(/\s+/g, "+");
	const fontLink = document.createElement("link");
	fontLink.id = fontLinkId;
	fontLink.rel = "stylesheet";
	fontLink.href = `https://fonts.googleapis.com/css2?family=${fontNameEncoded}:wght@300;400;500;600;700;800;900&display=swap`;
	document.head.appendChild(fontLink);
};

/**
 * Generate CSS for white-labeled colors
 * @param {object} tenantConfig - Tenant configuration object
 * @param {boolean} isJustmeds - Whether the current tenant is JustMeds
 * @returns {string} - Complete CSS string
 */
export const generateWhiteLabelStyles = (tenantConfig, isJustmeds = false) => {
	const primaryColorRaw = tenantConfig?.primary_color;
	const secondaryColorRaw = tenantConfig?.secondary_color;

	// Normalize hex colors (expand shorthand like #39f to #3399ff)
	const primaryColor = primaryColorRaw ? normalizeHex(primaryColorRaw) : null;
	const secondaryColor = secondaryColorRaw ? normalizeHex(secondaryColorRaw) : null;

	const primaryRgb = hexToRgb(primaryColor);
	const _secondaryRgb = hexToRgb(secondaryColor);

	// Get font family name if provided
	const fontFamily = tenantConfig?.font_family?.name;

	// Generate shades
	const primary100 = lightenColor(primaryColor, 0.9);
	const primary200 = lightenColor(primaryColor, 0.7);
	const primary300 = lightenColor(primaryColor, 0.5);
	const primary400 = lightenColor(primaryColor, 0.3);
	const primary500 = primaryColor; // Base color
	const primary600 = darkenColor(primaryColor, 0.1);
	const primary700 = darkenColor(primaryColor, 0.2);
	const _primary800 = darkenColor(primaryColor, 0.3);

	const yellow100 = lightenColor(secondaryColor, 0.9);
	const yellow200 = lightenColor(secondaryColor, 0.7);
	const yellow300 = lightenColor(secondaryColor, 0.5);
	const yellow400 = lightenColor(secondaryColor, 0.3);
	const yellow500 = secondaryColor; // Base color
	const yellow600 = darkenColor(secondaryColor, 0.1);
	const yellow700 = darkenColor(secondaryColor, 0.2);

	return `
    /* Primary Color Overrides */
    :root {
      --bs-primary: ${primaryColor};
      --bs-primary-rgb: ${primaryRgb};
      ${
			fontFamily
				? `--bs-font-sans-serif: "${fontFamily}", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;`
				: ""
		}
    }

    ${
		fontFamily
			? `
    /* Custom Font Family - Apply to everything */
    *,
    *::before,
    *::after {
      font-family: "${fontFamily}", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif !important;
    }
    
    body,
    html,
    input,
    textarea,
    select,
    button {
      font-family: "${fontFamily}", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif !important;
    }
    `
			: ""
	}

    /* Page and body backgrounds */
    body,
    .patient-intake-questions,
    .section.patient-intake {
      background-color: #FFFFFF !important;
    }

    /* Container backgrounds */
    .patient-intake-questions .container,
    .patient-intake .container {
      background-color: #FFFFFF !important;
    }

    .btn-primary {
      background-color: ${primary500} !important;
      border-color: ${primary500} !important;
      color: #fff !important;
    }
    .btn-primary:hover,
    .btn-primary:focus {
      background-color: ${primary600} !important;
      border-color: ${primary600} !important;
    }
    .btn-primary:active {
      background-color: ${primary700} !important;
      border-color: ${primary700} !important;
    }

    .btn-primary-100 {
      background-color: ${primary100} !important;
      border-color: ${primary200} !important;
    }
    .btn-primary-100:hover {
      background-color: ${primary200} !important;
      border-color: ${primary300} !important;
    }

    .btn-primary-200 {
      background-color: ${tenantConfig?.answered_questions_shade_color} !important;
      border-color: ${tenantConfig?.answered_questions_shade_color} !important;
    }
    .btn-primary-200:hover {
      background-color: ${primary200} !important;
      border-color: ${primary300} !important;
    }

    .border-primary-200 {
      border-color: ${primary200} !important;
    }

    .text-primary {
      color: ${primary500} !important;
    }

    .bg-primary {
      background-color: ${primary500} !important;
    }

    /* Primary Background Colors */
    .bg-primary-100 {
      background-color: ${primary100} !important;
    }
    .bg-primary-200 {
      background-color: ${primary200} !important;
    }
    .bg-primary-300 {
      background-color: ${primary300} !important;
    }
    .bg-primary-400 {
      background-color: ${primary400} !important;
    }

    /* Primary Border Colors */
    .border-primary {
      border-color: ${primary500} !important;
    }
    .border-primary-100 {
      border-color: ${primary100} !important;
    }
    .border-primary-300 {
      border-color: ${primary300} !important;
    }

    /* Info Color (also blue in Bootstrap) - map to primary */
    .bg-info {
      background-color: ${primary500} !important;
    }
    .bg-info-100 {
      background-color: ${primary100} !important;
    }
    .bg-info-200 {
      background-color: ${primary200} !important;
    }
    .text-info {
      color: ${primary500} !important;
    }
    .border-info {
      border-color: ${primary500} !important;
    }
    .btn-info {
      background-color: ${primary500} !important;
      border-color: ${primary500} !important;
      color: #fff !important;
    }
    .btn-info:hover,
    .btn-info:focus {
      background-color: ${primary600} !important;
      border-color: ${primary600} !important;
    }

    /* Alert variants */
    .alert-primary {
      background-color: ${primary100} !important;
      border-color: ${primary200} !important;
      color: ${primary700} !important;
    }
    .alert-info {
      background-color: ${primary100} !important;
      border-color: ${primary200} !important;
      color: ${primary700} !important;
    }

    /* Badge variants */
    .badge.bg-primary {
      background-color: ${primary500} !important;
    }
    .badge.bg-primary-200 {
      background-color: ${primary200} !important;
    }
    .badge.bg-info {
      background-color: ${primary500} !important;
    }

    /* Link colors */
    .link-primary {
      color: ${primary500} !important;
    }
    .link-primary:hover {
      color: ${primary600} !important;
    }

    /* Form check/radio colors */
    .form-check-input:checked {
      border-color: ${primary500} !important;
    }
    .form-check-input:focus {
      border-color: ${primary500} !important;
    }

    /* Enhanced Checkbox/Radio Styling */
    input[type="checkbox"]:checked,
    input[type="radio"]:checked {
      border-color: ${primary500} !important;
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' fill='%23${primary500.replace(
			"#",
			""
		)}' width='14px' height='14px'><rect x='1' y='1' width='12' height='12' rx='2' /></svg>"); !important;
    }

    input[type="checkbox"]:focus,
    input[type="radio"]:focus {
      border-color: ${primary500} !important;
    }

    /* Bootstrap form-check specific */
    .form-check-input[type="checkbox"]:checked,
    .form-check-input[type="radio"]:checked {
      border-color: ${primary500} !important;
    }

    /* Custom SOAP Section Colors */
    .bg-subjective,
    .bg-basic_subjective {
      background-color: #FFFFFF !important;
    }

    /* Answered Subjective Questions - use primary color shade for JustMeds */
    .bg-subjective-darken,
    .bg-basic_subjective-darken {
      background-color: ${tenantConfig?.answered_questions_shade_color} !important;
      border-color: ${primary400} !important;
    }

    /* Answered Objective Questions - use secondary color shade for JustMeds */
    .bg-objective-darken,
    .bg-basic_objective-darken {
      background-color: ${tenantConfig?.answered_questions_shade_color} !important;
      border-color: ${yellow400} !important;
    }

    .bg-objective,
    .bg-basic_objective {
      background-color: #FFFFFF !important;
    }

    .bg-assessment {
      background-color: #FFFFFF !important;
    }

    .bg-plan {
      background-color: #FFFFFF !important;
    }

    /* Accordion and card headers */
    // .accordion-button:not(.collapsed) {
    //   background-color: #FFFFFF !important;
    //   color: ${primary700} !important;
    // }

    /* Card with subjective class */
    .card.bg-subjective,
    .card .bg-subjective {
      background-color: #FFFFFF !important;
    }

    /* Progress bars */
    .progress-bar {
      background-color: ${primary500} !important;
    }
    .progress-bar.bg-primary {
      background-color: ${primary500} !important;
    }

    /* Secondary/Yellow Color Overrides */
    .btn-yellow,
    .btn-yellow-lg {
      background-color: ${yellow500} !important;
      border-color: ${yellow500} !important;
      color: #fff !important;
    }
    .btn-yellow:hover,
    .btn-yellow:focus,
    .btn-yellow-lg:hover,
    .btn-yellow-lg:focus {
      background-color: ${yellow600} !important;
      border-color: ${yellow600} !important;
    }
    .btn-yellow:active,
    .btn-yellow-lg:active {
      background-color: ${yellow700} !important;
      border-color: ${yellow700} !important;
    }

    .border-yellow-400 {
      border-color: ${yellow400} !important;
    }

    .text-yellow {
      color: ${yellow500} !important;
    }

    .bg-yellow {
      background-color: ${yellow500} !important;
    }

    /* Secondary Background Colors */
    .bg-yellow-100 {
      background-color: ${yellow100} !important;
    }
    .bg-yellow-200 {
      background-color: ${yellow200} !important;
    }
    .bg-yellow-300 {
      background-color: ${yellow300} !important;
    }
    .bg-yellow-400 {
      background-color: ${yellow400} !important;
    }

    /* Secondary Border Colors */
    .border-yellow {
      border-color: ${yellow500} !important;
    }
    .border-yellow-100 {
      border-color: ${yellow100} !important;
    }
    .border-yellow-200 {
      border-color: ${yellow200} !important;
    }
    .border-yellow-300 {
      border-color: ${yellow300} !important;
    }

    /* Warning color (also yellow in Bootstrap) - map to secondary */
    .bg-warning {
      background-color: ${yellow500} !important;
    }
    .text-warning {
      color: ${yellow500} !important;
    }
    .border-warning {
      border-color: ${yellow500} !important;
    }
    .btn-warning {
      background-color: ${yellow500} !important;
      border-color: ${yellow500} !important;
      color: #000 !important;
    }
    .btn-warning:hover,
    .btn-warning:focus {
      background-color: ${yellow600} !important;
      border-color: ${yellow600} !important;
    }

    /* Outline variants */
    .btn-outline-primary {
      color: ${primary500} !important;
      border-color: ${primary500} !important;
    }
    .btn-outline-primary:hover {
      background-color: ${primary500} !important;
      border-color: ${primary500} !important;
      color: #fff !important;
    }

    /* JustMeds Specific Styles */
    /* Treatment Initiated Question Background */
    .bg-treatment-initiated {
      background-color: #FFEB3B !important;
    }

    /* JustMeds - Make accordion headers and strong tags font-weight 500 */
    ${
		isJustmeds
			? `
    .bg-treatment-initiated .accordion-button strong,
    .bg-treatment-initiated .card-title strong,
    .bg-treatment-initiated strong {
      font-weight: 500 !important;
    }
    `
			: ""
	}

    /* Horizontal scroll fix for responsive view */

    // FontSizes
    h1{
      font-size: ${tenantConfig?.font_sizes?.page_header}px !important;
    }
    h2, .h5{
      font-size: ${tenantConfig?.font_sizes?.subheading}px !important;
    }
    .accordion-button{
      font-size: ${tenantConfig?.font_sizes?.category_accordion_title}px !important;
    }
    small{
      font-size: ${tenantConfig?.font_sizes?.answer_option}px !important;
    }
    .card-title-label{
      font-size: ${tenantConfig?.font_sizes?.question_label}px !important;
      ${isJustmeds ? "font-weight: 500 !important;" : ""}
    }
    button{
      font-size: ${tenantConfig?.font_sizes?.buttons}px !important;
    }
  `;
};
